import TOKEN_IMAGES, { FILE_IMAGES } from '@/assets/token-images';
import { DISPLAY_NAME_SYMBOLS } from '@/features/markets/utils/format';
import { getAllMarketStoreStates, MarketSpec } from '@/store/use-markets-store';
import { AssetType } from '@/types';

/**
 * @param symbol Full Symbol of the token from backend (*-PERP)
 * @returns Abbreviation of the token (Symbol without -PERP suffix)
 */
export const abbrFromSym = (
  symbol: string | undefined,
  options?: { raw?: boolean },
) => {
  if (!symbol) return '';
  const markets = getAllMarketStoreStates();
  const marketSpec = markets[symbol]?.marketSpec as MarketSpec | undefined;
  if (!marketSpec) return symbol.split('-USD-PERP')[0];
  if (DISPLAY_NAME_SYMBOLS.includes(symbol)) return marketSpec.displayName;
  const id = options?.raw ? symbol : marketSpec.displaySymbol;
  if (id.endsWith('-USD-PERP') && marketSpec.asset !== AssetType.FOREX)
    return id.split('-USD-PERP')[0];
  return id.split('-PERP')[0];
};

/**
 * @param symbol Full Symbol of the token from backend (*-PERP)
 * @returns URL of the token image
 */
export const imgFromSym = (symbol: string) => {
  let abbr = abbrFromSym(symbol, { raw: true });
  if (abbr.endsWith('.D')) {
    abbr = abbr.split('.D')[0] + 'DOM';
  }

  if (abbr in TOKEN_IMAGES) {
    return TOKEN_IMAGES[abbr as keyof typeof TOKEN_IMAGES];
  }

  if (FILE_IMAGES.includes(abbr)) return abbrToFile(abbr);

  const markets = getAllMarketStoreStates();
  const marketSpec = markets[symbol]?.marketSpec as MarketSpec | undefined;

  if (!marketSpec) return abbrToFile(symbol);

  switch (marketSpec.asset) {
    case AssetType.CRYPTO:
      if (abbr.startsWith('k')) {
        abbr = abbr.slice(1);
      }
      if (abbr == 'ANIME') {
        abbr = 'ANIMEC';
      }
      if (abbr == 'APT') {
        abbr = 'APTO';
      }
      if (abbr == 'HYPE') {
        abbr = 'HYPEH';
      }
      if (abbr == 'PENGU') {
        abbr = 'PENGUPUD';
      }
      if (abbr == 'S') {
        abbr = 'SONI';
      }
      if (abbr == 'SIGN') {
        abbr = 'SIGN2';
      }
      if (abbr == 'SOPH') {
        abbr = 'SOPHO';
      }
      if (abbr == 'SPX6900') {
        abbr = 'SPX6';
      }
      if (abbr == 'TST') {
        abbr = 'TSTTE';
      }
      if (abbr == 'WCT') {
        abbr = 'WCTW';
      }
      if (abbr == 'WLD') {
        abbr = 'WLDW';
      }
      if (abbr == 'ZK') {
        abbr = 'ZKSY';
      }
      if (abbr == 'ZRO') {
        abbr = 'ZROL';
      }
      return `https://s3-symbol-logo.tradingview.com/crypto/XTVC${abbr}--big.svg`;
    case AssetType.FOREX:
      const [curr] = abbr.split('-');
      return `https://s3-symbol-logo.tradingview.com/country/${curr.charAt(0)}${curr.charAt(1)}--big.svg`;
    default:
      return abbrToFile(symbol);
  }
};

export const symbolToBaseQuote = (symbol: string) => {
  return symbol.replace('-PERP', '/USD');
};

const abbrToFile = (abbr: string) => {
  const { pathname, href } = new URL(
    `../assets/token-images/${abbr}.png`,
    import.meta.url,
  );
  return pathname.includes('undefined')
    ? new URL('../assets/token-images/Default.png', import.meta.url).href
    : href;
};
